import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { Trash2, UserPlus, Building2, Sparkles, LayoutTemplate } from "lucide-react";

function SuperAdmin({ t, lang }) {
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [newOrg, setNewOrg] = useState("");
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", org_id: "", role: "employee" });
  const [landingTiles, setLandingTiles] = useState([]);
  const [newSentence, setNewSentence] = useState("");

  const load = useCallback(async () => {
    const [o, u, l] = await Promise.all([api.get("/admin/orgs"), api.get("/admin/users"), api.get("/admin/landing-tiles")]);
    setOrgs(o.data); setUsers(u.data); setLandingTiles(l.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const createSentence = async () => {
    if (!newSentence.trim()) return;
    try { await api.post("/admin/landing-tiles", { text: newSentence.trim() }); setNewSentence(""); load(); toast.success(t("saved")); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delSentence = async (id) => {
    try { await api.delete(`/admin/landing-tiles/${id}`); await load(); toast.success(t("deleted")); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const createOrg = async () => {
    if (!newOrg.trim()) return;
    try { await api.post("/admin/orgs", { name: newOrg.trim() }); setNewOrg(""); load(); toast.success(t("saved")); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const updateOrg = async (id, patch) => { await api.patch(`/admin/orgs/${id}`, patch); load(); };
  const delOrg = async (id) => {
    try { await api.delete(`/admin/orgs/${id}`); await load(); toast.success(t("deleted")); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const createUser = async () => {
    try { await api.post("/admin/users", newUser); setNewUser({ email: "", password: "", name: "", org_id: "", role: "employee" }); load(); toast.success(t("saved")); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const updateUser = async (id, patch) => { await api.patch(`/admin/users/${id}`, patch); load(); };
  const delUser = async (id) => {
    try { await api.delete(`/admin/users/${id}`); await load(); toast.success(t("deleted")); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <Tabs defaultValue="orgs">
      <TabsList className="mb-6">
        <TabsTrigger value="orgs" data-testid="tab-orgs"><Building2 className="mr-2 h-4 w-4" />{t("organizations")}</TabsTrigger>
        <TabsTrigger value="users" data-testid="tab-users"><UserPlus className="mr-2 h-4 w-4" />{t("users")}</TabsTrigger>
        <TabsTrigger value="landing" data-testid="tab-landing"><LayoutTemplate className="mr-2 h-4 w-4" />{t("landing_tiles")}</TabsTrigger>
      </TabsList>

      <TabsContent value="orgs">
        <div className="mb-4 flex gap-2">
          <Input data-testid="new-org-name" value={newOrg} onChange={(e) => setNewOrg(e.target.value)} placeholder={t("org_name")} className="max-w-xs rounded-full" />
          <Button data-testid="create-org-btn" onClick={createOrg} className="rounded-full">{t("create_org")}</Button>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{t("org_name")}</TableHead><TableHead>{t("members")}</TableHead>
              <TableHead>{t("paid_team")}</TableHead><TableHead>{t("paid_consultant")}</TableHead>
              <TableHead>{t("core_emotion")}</TableHead><TableHead>{t("actions")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {orgs.map((o) => (
                <TableRow key={o.id} data-testid={`org-row-${o.id}`}>
                  <TableCell className="font-semibold">{o.name}</TableCell>
                  <TableCell>{o.member_count}</TableCell>
                  <TableCell>
                    <Switch data-testid={`org-paid-${o.id}`} checked={!!o.is_paid} onCheckedChange={(v) => updateOrg(o.id, { is_paid: v })} />
                  </TableCell>
                  <TableCell>
                    <Switch data-testid={`org-consultant-${o.id}`} checked={!!o.consultant_paid} onCheckedChange={(v) => updateOrg(o.id, { consultant_paid: v })} />
                  </TableCell>
                  <TableCell>
                    <Input defaultValue={o.core_emotion || ""} data-testid={`org-core-${o.id}`} className="h-8 w-32 rounded-full"
                      onBlur={(e) => updateOrg(o.id, { core_emotion: e.target.value })} placeholder="—" />
                  </TableCell>
                  <TableCell>
                    <button type="button" data-testid={`org-del-${o.id}`} onClick={() => delOrg(o.id)}><Trash2 className="h-4 w-4 text-destructive" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="users">
        <div className="mb-4 grid gap-2 rounded-2xl border border-border bg-card p-4 sm:grid-cols-5">
          <Input data-testid="nu-name" placeholder={t("name")} value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
          <Input data-testid="nu-email" placeholder={t("email")} value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
          <Input data-testid="nu-pass" placeholder={t("password")} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
          <select data-testid="nu-org" className="rounded-md border border-input bg-background px-3 text-sm" value={newUser.org_id} onChange={(e) => setNewUser({ ...newUser, org_id: e.target.value })}>
            <option value="">{t("organizations")}…</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <Button data-testid="create-user-btn" onClick={createUser} className="rounded-full">{t("create_user")}</Button>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{t("name")}</TableHead><TableHead>{t("email")}</TableHead><TableHead>{t("role")}</TableHead><TableHead>{t("actions")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-semibold">{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <select data-testid={`user-role-${u.id}`} className="rounded-md border border-input bg-background px-2 py-1 text-sm"
                      value={u.role} onChange={(e) => updateUser(u.id, { role: e.target.value })}>
                      <option value="employee">employee</option>
                      <option value="org_admin">org_admin</option>
                      <option value="superadmin">superadmin</option>
                    </select>
                  </TableCell>
                  <TableCell>
                    <button type="button" data-testid={`user-del-${u.id}`} onClick={() => delUser(u.id)}><Trash2 className="h-4 w-4 text-destructive" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="landing">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row">
          <Input data-testid="new-sentence" value={newSentence} onChange={(e) => setNewSentence(e.target.value)}
            placeholder={t("sentence")} className="rounded-full"
            onKeyDown={(e) => { if (e.key === "Enter") createSentence(); }} />
          <Button data-testid="create-sentence-btn" onClick={createSentence} className="shrink-0 rounded-full">{t("add_sentence")}</Button>
        </div>
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow><TableHead>{t("sentence")}</TableHead><TableHead className="w-20">{t("actions")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {landingTiles.length === 0 && (
                <TableRow><TableCell colSpan={2} className="text-muted-foreground">—</TableCell></TableRow>
              )}
              {landingTiles.map((s) => (
                <TableRow key={s.id} data-testid={`sentence-row-${s.id}`}>
                  <TableCell className="font-tile text-lg italic text-primary">{s.text}</TableCell>
                  <TableCell>
                    <button type="button" data-testid={`sentence-del-${s.id}`} onClick={() => delSentence(s.id)}><Trash2 className="h-4 w-4 text-destructive" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </TabsContent>
    </Tabs>
  );
}

function OrgAdmin({ t }) {
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "employee" });
  const [payBusy, setPayBusy] = useState(false);

  const load = useCallback(async () => { const { data } = await api.get("/org/me"); setOrg(data); setForm((f) => ({ ...f, org_id: data.id })); }, []);
  useEffect(() => { load(); }, [load]);
  if (!org) return <p className="text-muted-foreground">{t("processing")}</p>;

  const upgrade = async () => {
    setPayBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: "employees", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setPayBusy(false); }
  };

  const add = async () => {
    try { await api.post("/org/employees", { ...form, org_id: org.id }); setForm({ email: "", password: "", name: "", role: "employee" }); load(); toast.success(t("saved")); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const setRole = async (uid, role) => { await api.patch(`/org/employees/${uid}/role`, { role }); load(); };

  return (
    <div className="space-y-6">
      {!org.is_paid && (
        <div className="flex flex-col gap-3 rounded-2xl border border-accent/30 bg-accent/5 p-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-foreground">{t("upgrade_sub")}</p>
          <Button data-testid="orgadmin-upgrade-1000-button" onClick={upgrade} disabled={payBusy}
            className="shrink-0 rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
            <Sparkles className="mr-2 h-4 w-4" /> {t("upgrade_cta")}
          </Button>
        </div>
      )}
      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="mb-1 font-display text-lg font-semibold text-primary">{t("add_employee")}</h3>
        <p className="mb-4 text-xs text-muted-foreground">{t("cannot_remove")}</p>
        <div className="grid gap-2 sm:grid-cols-4">
          <Input data-testid="emp-name" placeholder={t("name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input data-testid="emp-email" placeholder={t("email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input data-testid="emp-pass" placeholder={t("password")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <Button data-testid="add-emp-btn" onClick={add} disabled={!org.is_paid} className="rounded-full"><UserPlus className="mr-2 h-4 w-4" />{t("add")}</Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-border bg-card">
        <Table>
          <TableHeader><TableRow><TableHead>{t("name")}</TableHead><TableHead>{t("email")}</TableHead><TableHead>{t("role")}</TableHead></TableRow></TableHeader>
          <TableBody>
            {org.members.map((m) => (
              <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                <TableCell className="font-semibold">{m.name}</TableCell>
                <TableCell>{m.email}</TableCell>
                <TableCell>
                  <select data-testid={`member-role-${m.id}`} className="rounded-md border border-input bg-background px-2 py-1 text-sm"
                    value={m.role} onChange={(e) => setRole(m.id, e.target.value)}>
                    <option value="employee">employee</option>
                    <option value="org_admin">org_admin</option>
                  </select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function Admin() {
  const { t, lang } = useLang();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useDocumentTitle(t("admin"));
  useEffect(() => {
    if (!loading && (!user || (user.role !== "superadmin" && user.role !== "org_admin"))) nav("/journey");
  }, [loading, user, nav]);
  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-6xl px-5 py-8">
        <h1 className="mb-6 font-display text-3xl font-bold text-primary">{t("admin")}</h1>
        {user.role === "superadmin" ? <SuperAdmin t={t} lang={lang} /> : <OrgAdmin t={t} />}
      </div>
    </div>
  );
}
